import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, Plus, Eye, IndianRupee, RefreshCw,
  Calendar, Building2, CreditCard, Banknote, Smartphone, FileText,
  Download, TrendingUp, Wallet
} from 'lucide-react';

const PaymentCollections = () => {
  const { user } = useAuth();
  const location = useLocation();
  const preselectedBill = location.state?.bill;

  // Data states
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [pendingBills, setPendingBills] = useState([]);
  
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [advanceFilter, setAdvanceFilter] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    customer_id: '',
    bill_id: '',
    amount: '',
    payment_mode: 'Cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: '',
    is_advance: false
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  // Fetch collections
  const fetchCollections = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      if (paymentModeFilter !== 'all') params.payment_mode = paymentModeFilter;
      if (advanceFilter !== 'all') params.is_advance = advanceFilter === 'advance';
      
      const response = await api.get('/payment-collections', { params });
      setCollections(response.data);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  }, [fromDate, toDate, searchTerm, paymentModeFilter, advanceFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const response = await api.get('/payment-collections/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [fromDate, toDate]);

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers', { params: { status: 'Active' } });
        setCustomers(response.data);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch pending bills for selected customer
  const fetchPendingBills = useCallback(async () => {
    if (!selectedCustomer) {
      setPendingBills([]);
      return;
    }
    
    try {
      const response = await api.get('/sales-billing/pending-payments', {
        params: { customer_id: selectedCustomer.id }
      });
      setPendingBills(response.data);
    } catch (error) {
      console.error('Error fetching pending bills:', error);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (canAccess) {
      fetchCollections();
      fetchStats();
    }
  }, [canAccess, fetchCollections, fetchStats]);

  useEffect(() => {
    fetchPendingBills();
  }, [fetchPendingBills]);

  // Handle preselected bill
  useEffect(() => {
    if (preselectedBill && customers.length > 0) {
      const customer = customers.find(c => c.id === preselectedBill.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.company_name);
        setFormData(prev => ({
          ...prev,
          customer_id: customer.id,
          bill_id: preselectedBill.id,
          amount: preselectedBill.pending_amount || (preselectedBill.net_amount - (preselectedBill.amount_paid || 0))
        }));
        setCreateDialogOpen(true);
      }
    }
  }, [preselectedBill, customers]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchCollections(), fetchStats()])
      .finally(() => setLoading(false));
  };

  // Filter customers
  const filteredCustomers = customers.filter(c => 
    c.company_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch)
  ).slice(0, 10);

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.company_name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      bill_id: ''
    }));
  };

  // Open create dialog
  const openCreateDialog = () => {
    setFormData({
      customer_id: '',
      bill_id: '',
      amount: '',
      payment_mode: 'Cash',
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: '',
      is_advance: false
    });
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCreateDialogOpen(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    const parsedAmount = parseFloat(formData.amount);
    if (formData.amount === '' || isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    // Only allow 0 amount if linked to a bill (to close 0-amount bills)
    if (parsedAmount === 0 && !formData.bill_id) {
      toast.error('Amount must be greater than 0 for advance payments');
      return;
    }
    
    if (!formData.is_advance && !formData.bill_id) {
      toast.error('Please select a bill or mark as advance payment');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        bill_id: formData.is_advance ? null : formData.bill_id,
        amount: parseFloat(formData.amount),
        payment_mode: formData.payment_mode,
        payment_date: formData.payment_date,
        reference_number: formData.reference_number || '',
        notes: formData.notes || '',
        is_advance: formData.is_advance
      };
      
      const response = await api.post('/payment-collections', payload);
      toast.success(`Payment recorded - ${response.data.collection?.collection_no}`);
      setCreateDialogOpen(false);
      fetchCollections();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  // View collection details
  const handleViewCollection = async (collection) => {
    try {
      const response = await api.get(`/payment-collections/${collection.id}`);
      setSelectedCollection(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };

  // Delete collection
  const handleDelete = async (collection) => {
    if (!isAdmin) {
      toast.error('Only Admin can delete payments');
      return;
    }
    
    if (!window.confirm(`Delete payment ${collection.collection_no}?`)) {
      return;
    }
    
    try {
      await api.delete(`/payment-collections/${collection.id}`);
      toast.success('Payment deleted');
      fetchCollections();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  // Get payment mode icon
  const getPaymentModeIcon = (mode) => {
    switch (mode) {
      case 'Cash': return <Banknote size={14} className="text-green-600" />;
      case 'UPI': return <Smartphone size={14} className="text-purple-600" />;
      case 'Bank Transfer': return <Building2 size={14} className="text-blue-600" />;
      case 'Cheque': return <FileText size={14} className="text-orange-600" />;
      default: return <CreditCard size={14} />;
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only Admin and Backoffice Accounts Executive can access Payment Collections.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-collections">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Collections</h1>
          <p className="text-gray-600 mt-1">Record and track payment collections</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} disabled={loading} variant="outline">
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700">
            <Plus size={18} className="mr-2" />
            Record Payment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Today's Collection</p>
                <p className="text-3xl font-bold text-green-800">₹{(stats.today_collection || 0).toLocaleString()}</p>
              </div>
              <IndianRupee className="h-10 w-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Collected</p>
                <p className="text-3xl font-bold text-blue-800">₹{(stats.total_collected || 0).toLocaleString()}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Bill Payments</p>
                <p className="text-3xl font-bold text-purple-800">₹{(stats.bill_payments || 0).toLocaleString()}</p>
              </div>
              <FileText className="h-10 w-10 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Advance Payments</p>
                <p className="text-3xl font-bold text-orange-800">₹{(stats.advance_payments || 0).toLocaleString()}</p>
              </div>
              <Wallet className="h-10 w-10 text-orange-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total_transactions || 0}</p>
              </div>
              <CreditCard className="h-10 w-10 text-gray-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Mode Breakdown */}
      {stats.by_payment_mode && Object.keys(stats.by_payment_mode).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Collection by Payment Mode</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.by_payment_mode).map(([mode, amount]) => (
                <div key={mode} className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
                  {getPaymentModeIcon(mode)}
                  <span className="font-medium">{mode}:</span>
                  <span className="text-green-700">₹{amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search by collection #, customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Calendar size={18} className="text-gray-400" />
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
          />
          <span className="text-gray-400">to</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
          />
        </div>
        <Select value={paymentModeFilter} onValueChange={setPaymentModeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Payment Mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modes</SelectItem>
            <SelectItem value="Cash">Cash</SelectItem>
            <SelectItem value="UPI">UPI</SelectItem>
            <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
            <SelectItem value="Cheque">Cheque</SelectItem>
          </SelectContent>
        </Select>
        <Select value={advanceFilter} onValueChange={setAdvanceFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="bill">Bill Payments</SelectItem>
            <SelectItem value="advance">Advance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Collections Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Records</CardTitle>
          <CardDescription>All payment collections (latest first)</CardDescription>
        </CardHeader>
        <CardContent>
          {collections.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No payment records found</p>
              <Button onClick={openCreateDialog} className="mt-4" variant="outline">
                <Plus size={16} className="mr-2" />
                Record First Payment
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Collection #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collections.map((collection) => (
                    <TableRow key={collection.id} data-testid={`collection-${collection.id}`}>
                      <TableCell className="whitespace-nowrap">
                        {collection.payment_date}
                      </TableCell>
                      <TableCell className="font-medium">{collection.collection_no}</TableCell>
                      <TableCell>{collection.customer_name}</TableCell>
                      <TableCell>{collection.customer_mobile}</TableCell>
                      <TableCell>
                        {collection.bill_id ? (
                          <span className="text-blue-600">{collection.bill?.bill_no || 'Bill'}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getPaymentModeIcon(collection.payment_mode)}
                          <span>{collection.payment_mode}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-green-700">
                        ₹{(collection.amount || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {collection.is_advance ? (
                          <Badge className="bg-orange-100 text-orange-800">Advance</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800">Bill Payment</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {collection.reference_number || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewCollection(collection)}
                          >
                            <Eye size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Payment Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee size={20} />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              Record a new payment collection from customer
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Customer Search */}
              <div className="space-y-2">
                <Label>Customer *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    placeholder="Search customer..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                      if (!e.target.value) {
                        setSelectedCustomer(null);
                        setFormData(prev => ({ ...prev, customer_id: '', bill_id: '' }));
                      }
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="pl-9"
                  />
                  
                  {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredCustomers.map(customer => (
                        <div
                          key={customer.id}
                          className="p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectCustomer(customer)}
                        >
                          <p className="font-medium text-sm">{customer.company_name}</p>
                          <p className="text-xs text-gray-500">{customer.mobile} • {customer.city}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Type Toggle */}
              <div className="space-y-2">
                <Label>Payment Type *</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={!formData.is_advance ? 'default' : 'outline'}
                    className={!formData.is_advance ? 'bg-blue-600' : ''}
                    onClick={() => setFormData(prev => ({ ...prev, is_advance: false }))}
                  >
                    Against Bill
                  </Button>
                  <Button
                    type="button"
                    variant={formData.is_advance ? 'default' : 'outline'}
                    className={formData.is_advance ? 'bg-orange-600' : ''}
                    onClick={() => setFormData(prev => ({ ...prev, is_advance: true, bill_id: '' }))}
                  >
                    Advance Payment
                  </Button>
                </div>
              </div>

              {/* Bill Selection (if not advance) */}
              {!formData.is_advance && selectedCustomer && (
                <div className="space-y-2">
                  <Label>Select Bill *</Label>
                  {pendingBills.length === 0 ? (
                    <p className="text-sm text-gray-500">No pending bills for this customer</p>
                  ) : (
                    <Select 
                      value={formData.bill_id} 
                      onValueChange={(v) => {
                        const bill = pendingBills.find(b => b.id === v);
                        setFormData(prev => ({ 
                          ...prev, 
                          bill_id: v,
                          amount: bill ? (bill.pending_amount || bill.net_amount - (bill.amount_paid || 0)) : ''
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a pending bill" />
                      </SelectTrigger>
                      <SelectContent>
                        {pendingBills.map(bill => (
                          <SelectItem key={bill.id} value={bill.id}>
                            {bill.bill_no} - ₹{(bill.pending_amount || bill.net_amount - (bill.amount_paid || 0)).toLocaleString()} pending
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  required
                />
                <p className="text-xs text-gray-500">Decimal values allowed (e.g., 17500.70)</p>
              </div>

              {/* Payment Mode */}
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select 
                  value={formData.payment_mode} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, payment_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">💵 Cash</SelectItem>
                    <SelectItem value="UPI">📱 UPI</SelectItem>
                    <SelectItem value="Bank Transfer">🏦 Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">📄 Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Date */}
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                  required
                />
              </div>

              {/* Reference Number */}
              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="Transaction ID, Cheque #, etc."
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !formData.customer_id || !formData.amount}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? 'Recording...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Collection Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment Details - {selectedCollection?.collection_no}</DialogTitle>
          </DialogHeader>
          {selectedCollection && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <p className="text-sm text-green-600">Amount Collected</p>
                <p className="text-3xl font-bold text-green-800">₹{(selectedCollection.amount || 0).toLocaleString()}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Customer:</span>
                  <p className="font-medium">{selectedCollection.customer?.company_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Mobile:</span>
                  <p>{selectedCollection.customer?.mobile}</p>
                </div>
                <div>
                  <span className="text-gray-500">Payment Date:</span>
                  <p>{selectedCollection.payment_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">Payment Mode:</span>
                  <p className="flex items-center gap-1">
                    {getPaymentModeIcon(selectedCollection.payment_mode)}
                    {selectedCollection.payment_mode}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <p>
                    {selectedCollection.is_advance ? (
                      <Badge className="bg-orange-100 text-orange-800">Advance Payment</Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-800">Bill Payment</Badge>
                    )}
                  </p>
                </div>
                {selectedCollection.bill && (
                  <div>
                    <span className="text-gray-500">Bill #:</span>
                    <p className="text-blue-600">{selectedCollection.bill.bill_no}</p>
                  </div>
                )}
                {selectedCollection.reference_number && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Reference:</span>
                    <p>{selectedCollection.reference_number}</p>
                  </div>
                )}
                {selectedCollection.notes && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Notes:</span>
                    <p>{selectedCollection.notes}</p>
                  </div>
                )}
                <div className="col-span-2 text-xs text-gray-400 border-t pt-2">
                  Recorded by {selectedCollection.created_by_name} on {selectedCollection.created_at?.split('T')[0]}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentCollections;
